import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDateTime, getSpeakerColor, newFormatTime } from '../../../../utils/calcs';
import { getTeamUuid } from '../../../../utils/cookies';
import Cookies from "universal-cookie";
import { ArrowLeftIcon, SpeakerWaveIcon, DocumentTextIcon, CheckIcon, ListBulletIcon, Bars2Icon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
// Note: Now using single endpoint instead of multiple imports
import { Card } from '@tremor/react';
import OverallSentimentCard from '../../../../Components/NewAnalysisElements/OverallSentimentCard';
import { mapSentimentAndColors, sentimentTranslations } from '../../../../utils/mappings';
import OverallParticipationCard from '../../../../Components/NewAnalysisElements/OverallParticipationCard';
import TextSectionCard from '../../../../Components/NewAnalysisElements/TextSectionCard';
import TimeBasedParticipationCard from '../../../../Components/NewAnalysisElements/TimeBasedParticipationCard';
import TimeBasedSentimentCard from '../../../../Components/NewAnalysisElements/TimeBasedSentimentCard';
import PolarAreaCenterLabelsChart from '../../../../Components/Results/PolarAreaCenterLabelsChart';
// Research pipeline specific components
import ResearchPipelinePresenceMatrix from '../../../../Components/Results/ResearchPipelinePresenceMatrix';
import ResearchPipelinePolarCharts from '../../../../Components/Results/ResearchPipelinePolarCharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PrinterIcon } from '@heroicons/react/24/outline';
import ErrorBoundary from '../../../../Components/ErrorBoundary/ErrorBoundary';
import ParticleLoader from "../../../../Components/ParticleLoader";

// Helper function to get auth token
function getToken() {
    const cookies = new Cookies();
    return cookies.get("token");
}

// Colors array for speaker color mapping - same as TextCoder
const colors = [
    "#A7C7E7",
    "#F3A683",
    "#F7D794",
    "#B8E0D2",
    "#E77F67",
    "#786FA6",
    "#F8A5C2",
    "#63C7B2",
    "#EFA8E4",
    "#F19066",
    "#F3EAC2",
    "#F5D6BA",
    "#C5D1EB",
    "#D6AD60",
    "#92B4A7",
];

// Individual Analysis Component - Replicates NewAnalysisHolder structure
function IndividualAnalysis({ file, index, t }) {
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Speaker color functions from NewAnalysisHolder
    const SpeakerColors = [
        "emerald-500", "yellow-500", "purple-500", "red-500", "lime-500",
        "zinc-500", "gray-500", "indigo-500", "orange-500", "black",
        "teal-500", "cyan-500",
    ];

    const getSpeakerColorRaw = (speaker) => {
        const letter = (speaker.split(' ')[1]).split('-')[0];
        if (!letter) return SpeakerColors[0];
        const index = letter.charCodeAt(0) - 'A'.charCodeAt(0);
        return SpeakerColors[index % SpeakerColors.length];
    };

    const getSpeakerColor = (letter) => {
        const index = letter.charCodeAt(0) - 'A'.charCodeAt(0);
        return SpeakerColors[index % SpeakerColors.length];
    };

    const getLegendData = (sentimentData) => {
        const parsedData = JSON.parse(sentimentData);
        const letters = [];
        Object.keys(parsedData).forEach(speaker => {
            const parts = speaker?.includes('Speaker ') ? speaker.split(' ')[1] : speaker;
            const letter = parts.split('-')[0];
            if (!letters?.includes(letter)) {
                letters.push(letter);
            }
        });
        return letters.map((letter) => {
            return {
                label: `Speaker ${letter}`,
                color: getSpeakerColor(letter)
            };
        });
    };

    useEffect(() => {
        const loadAnalysis = () => {
            if (file.status !== 'Processed') {
                setError(t('PipelineResults.fileNotProcessed'));
                setLoading(false);
                return;
            }

            try {
                // Get analysis data from the globally cached data
                const analysisData = window.pipelineAnalysisData?.[file.uuid];
                
                if (analysisData) {
                    // Check if duration_seconds exists and use it as duration
                    const analysisWithDuration = {
                        ...analysisData,
                        duration: analysisData.duration || analysisData.duration_seconds || analysisData.audio_duration || 0
                    };
                    setAnalysis(analysisWithDuration);
                } else {
                    setError(t('PipelineResults.analysisDataNotAvailable'));
                }
                
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        loadAnalysis();
    }, [file.uuid, file.status]);

    if (loading) {
        return (
            <div className="bg-white rounded-2xl w-full shadow-xl mb-8 p-8">
                <div className="text-center py-12">
                    <div className="text-gray-400 text-4xl mb-4">⏳</div>
                    <div className="text-gray-600 mb-2">{t('PipelineResults.loadingTitle')}</div>
                    <div className="text-sm text-gray-500">File: {file.name}</div>
                </div>
            </div>
        );
    }

    if (error || !analysis) {
        return (
            <div className="bg-white rounded-2xl w-full shadow-xl mb-8 p-8">
                <div className="text-center py-12">
                    <div className="text-red-400 text-4xl mb-4">❌</div>
                    <div className="text-red-600 mb-2">{t('PipelineResults.errorLoadingResults')}</div>
                    <div className="text-sm text-gray-500 mb-4">{error}</div>
                    <a 
                        href={`/analysis/${file.uuid}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-amber-500 hover:underline"
                    >
                        {t('PipelineResults.tryOpeningAnalysisPage')}
                    </a>
                </div>
            </div>
        );
    }

    // Parse speaker names if available
    let speakerNames = null;
    if (analysis?.speaker_names) {
        try {
            // Check if it's already an object or if it needs parsing
            if (typeof analysis.speaker_names === 'string') {
                speakerNames = JSON.parse(analysis.speaker_names);
            } else if (typeof analysis.speaker_names === 'object') {
                speakerNames = analysis.speaker_names;
            }
        } catch (e) {
            speakerNames = {};
        }
    } else {
        speakerNames = {};
    }

    // Create legend data for charts
    const legendData = () => {
        return Object.entries(speakerNames || {}).map(([key, name]) => ({
            name: name,
            color: getSpeakerColor(key.split(' ')[1] || 'A')
        }));
    };

    return (
        <div className="bg-white rounded-2xl w-full shadow-xl mb-8">
            {/* Analysis Header - Same as NewAnalysisHolder */}
            <div className="shadow-2xl px-4 py-4 w-full bg-white rounded-2xl">
                <div className="flex justify-between flex-col md:flex-row mb-4">
                    <div className="flex items-center justify-start mb-4 ml-1">
                        {analysis.file_type === "audio" && <SpeakerWaveIcon className="w-10 h-10" />}
                        {analysis.file_type === "file" && <DocumentTextIcon className="w-10 h-10" />}
                        <div className="pl-4">
                            <div className="text-base font-semibold text-gray-900">
                                {analysis.name || file.name || `Analysis ${index + 1}`}
                            </div>
                            {analysis.created_at && (
                                <div className="text-sm text-gray-500">
                                    {formatDateTime(analysis.created_at)}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <a 
                            href={`/analysis/${file.uuid}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
                        >
                            {t('PipelineResults.openInteractiveAnalysis')}
                        </a>
                    </div>
                </div>

                {/* Analysis Fragments Grid - Same as NewAnalysisHolder */}
                <div className="analysis-cards grid grid-cols-1 md:grid-cols-2 gap-2 pb-8">
                    {/* Overall Sentiment and Participation Cards */}
                    {analysis.fragments?.overall_sentiment && (
                        <div className="flex-1 p-4 bg-gray-50">
                            <ErrorBoundary minimal>
                                <OverallSentimentCard 
                                    fragment={{
                                        type: "overall_sentiment",
                                        result: typeof analysis.fragments.overall_sentiment === 'string' 
                                            ? analysis.fragments.overall_sentiment 
                                            : JSON.stringify(analysis.fragments.overall_sentiment)
                                    }}
                                    t={t}
                                    sentimentTranslations={sentimentTranslations}
                                    mapSentimentAndColors={mapSentimentAndColors}
                                />
                            </ErrorBoundary>
                        </div>
                    )}
                    
                    {analysis.fragments?.overall_participation && (
                        <div className="flex-1 p-4 bg-gray-50">
                            <ErrorBoundary minimal>
                                <OverallParticipationCard 
                                    duration={analysis.duration || 0}
                                    fragment={{
                                        type: "overall_participation",
                                        result: typeof analysis.fragments.overall_participation === 'string'
                                            ? analysis.fragments.overall_participation
                                            : JSON.stringify(analysis.fragments.overall_participation)
                                    }}
                                    t={t}
                                    speakerNames={speakerNames}
                                />
                            </ErrorBoundary>
                        </div>
                    )}

                    {/* Other Analysis Cards */}
                    {analysis.fragments?.text_summary && (
                        <div className="col-span-2">
                            <TextSectionCard
                                icon={Bars2Icon}
                                title={t('ExpandedMeedingCard.SummaryLabel')}
                                content={analysis.fragments.text_summary}
                                meeting_uuid={analysis.uuid}
                                lines={20}
                                content_name="text_summary"
                                updateFragmentState={() => {}} // Read-only
                                speakerNames={speakerNames}
                                view_mode="public"
                            />
                        </div>
                    )}
                    
                    {analysis.fragments?.bullets_summary && (
                        <div className="col-span-2">
                            <TextSectionCard
                                icon={ListBulletIcon}
                                title={t('ExpandedMeedingCard.SummaryByPointsLabel')}
                                content={analysis.fragments.bullets_summary}
                                meeting_uuid={analysis.uuid}
                                lines={20}
                                content_name="bullets_summary"
                                updateFragmentState={() => {}} // Read-only
                                speakerNames={speakerNames}
                                view_mode="public"
                            />
                        </div>
                    )}
                    
                    {analysis.fragments?.tasks && (
                        <div className="col-span-2">
                            <TextSectionCard
                                icon={CheckIcon}
                                title={t('ExpandedMeedingCard.TasksLabel')}
                                content={analysis.fragments.tasks}
                                meeting_uuid={analysis.uuid}
                                lines={15}
                                content_name="tasks"
                                updateFragmentState={() => {}} // Read-only
                                speakerNames={speakerNames}
                                view_mode="public"
                            />
                        </div>
                    )}
                    
                    {analysis.fragments?.time_based_participation && (
                        <div className="col-span-2">
                            <TimeBasedParticipationCard
                                duration={analysis.duration || 0}
                                fragment={{result: JSON.stringify(analysis.fragments.time_based_participation)}}
                                t={t}
                                getSpeakerColorRaw={getSpeakerColorRaw}
                                getLegendData={() => getLegendData(JSON.stringify(analysis.fragments.overall_sentiment || {}))}
                                speakerNames={speakerNames}
                            />
                        </div>
                    )}
                    
                    {analysis.fragments?.time_based_sentiment && (
                        <div className="col-span-2">
                            <TimeBasedSentimentCard
                                duration={analysis.duration || 0}
                                fragment={{result: JSON.stringify(analysis.fragments.time_based_sentiment)}}
                                t={t}
                                sentimentTranslations={sentimentTranslations}
                                mapSentimentAndColors={mapSentimentAndColors}
                            />
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

// Full Transcription Display Component - Replicates TextCoder functionality
function FullTranscriptionDisplay({ file, textCodes, fileIndex, t }) {
    const [transcriptionData, setTranscriptionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [speakerNames, setSpeakerNames] = useState({});
    const [isExpanded, setIsExpanded] = useState(false);

    // Handle print events to expand transcriptions
    useEffect(() => {
        const handleBeforePrint = () => {
            setIsExpanded(true);
        };

        window.addEventListener('beforeprint', handleBeforePrint);

        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
        };
    }, []);

    useEffect(() => {
        const loadTranscription = () => {
            if (file.status !== 'Processed') {
                setError(t('PipelineResults.fileNotProcessed'));
                setLoading(false);
                return;
            }

            try {
                // Get analysis data from the globally cached data
                const analysis = window.pipelineAnalysisData?.[file.uuid];
                
                if (!analysis) {
                    setError(t('PipelineResults.analysisDataNotAvailable'));
                    setLoading(false);
                    return;
                }
                
                if (analysis.speaker_names) {
                    setSpeakerNames(analysis.speaker_names);
                }

                // Get the transcription fragment from the new data structure
                const transcriptionFragment = analysis.fragments?.edited_transcription;

                if (transcriptionFragment) {
                    // Parse transcription like in TextCoder with better parsing
                    const lines = transcriptionFragment.split('\n').map((line) => {
                        const uuidMatch = line.match(/^<([^>]+)>/);
                        const uuid = uuidMatch ? uuidMatch[1] : "";
                        const lineWithoutUuid = uuid ? line.replace(/^<[^>]+>/, '').trim() : line;
                        
                        // Better regex to capture time format [start-end] Speaker X:
                        const fullMatch = lineWithoutUuid.match(/^(\[([^\]]+)\])\s+(Speaker\s+[A-Z](?:-[^\s]*)?):(.*)$/);
                        
                        if (fullMatch) {
                            const timeStamp = fullMatch[1]; // [start-end]
                            const timeRange = fullMatch[2]; // start-end (without brackets)
                            const speakerKey = fullMatch[3]; // Speaker X or Speaker X-Name
                            const content = fullMatch[4].trim(); // The actual speech
                            
                            // Get speaker display name from speakerNames mapping
                            const speakerDisplayName = speakerNames[speakerKey] || speakerKey;
                            
                            // Parse time range for display like in TextCoder
                            let startTime = '', endTime = '';
                            if (timeRange) {
                                const times = timeRange.split('-');
                                if (times.length === 2) {
                                    startTime = times[0].trim();
                                    endTime = times[1].trim();
                                }
                            }
                            
                            return {
                                uuid,
                                timeStamp,
                                timeRange,
                                startTime,
                                endTime,
                                speakerKey,
                                speakerDisplayName,
                                staticPart: `${timeStamp} ${speakerDisplayName}:`,
                                editablePart: content,
                                fullLine: lineWithoutUuid
                            };
                        }
                        
                        // Fallback parsing for lines that don't match the expected format
                        const parts = lineWithoutUuid.split(":");
                        if (parts.length >= 2) {
                            const timeAndSpeaker = parts[0].trim();
                            const content = parts.slice(1).join(":").trim();
                            
                            return {
                                uuid,
                                timeStamp: "",
                                speakerKey: timeAndSpeaker,
                                speakerDisplayName: timeAndSpeaker,
                                staticPart: `${timeAndSpeaker}:`,
                                editablePart: content,
                                fullLine: lineWithoutUuid
                            };
                        }
                        
                        return null;
                    }).filter(line => line && line.editablePart);

                    setTranscriptionData(lines);
                } else {
                    setError(t('PipelineResults.noTranscriptionFound'));
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadTranscription();
    }, [file.uuid, file.status]);

    const renderTextWithHighlights = (lineData, text) => {
        if (!textCodes || textCodes.length === 0) {
            return <span className="whitespace-pre-wrap">{text}</span>;
        }

        // Find all excerpts that match this line's UUID or contain this text
        const relevantExcerpts = [];
        textCodes.forEach(code => {
            if (code.excerpts && code.excerpts.length > 0) {
                code.excerpts.forEach(excerpt => {
                    const excerptText = excerpt.text || excerpt.content || excerpt.excerpt_text || excerpt.body || '';
                    
                    // Multiple ways to match excerpts to transcription lines:
                    // 1. UUID match (most reliable)
                    // 2. Text content match (fuzzy matching)
                    // 3. Analysis UUID match
                    let matchFound = false;
                    let startIndex = -1;
                    
                    if (excerpt.transcription_uuid === lineData.uuid) {
                        // Direct UUID match - try to find the text
                        startIndex = text.toLowerCase().indexOf(excerptText.toLowerCase().trim());
                        matchFound = startIndex !== -1;
                    }
                    
                    if (!matchFound && excerptText.length > 10) {
                        // Fuzzy text matching for longer excerpts
                        const cleanExcerptText = excerptText.toLowerCase().trim().replace(/\s+/g, ' ');
                        const cleanLineText = text.toLowerCase().trim().replace(/\s+/g, ' ');
                        
                        // Try exact match first
                        startIndex = cleanLineText.indexOf(cleanExcerptText);
                        if (startIndex !== -1) {
                            matchFound = true;
                        } else {
                            // Try partial matches (at least 80% of the excerpt)
                            const minMatchLength = Math.floor(cleanExcerptText.length * 0.8);
                            for (let i = 0; i <= cleanLineText.length - minMatchLength; i++) {
                                const substring = cleanLineText.substring(i, i + cleanExcerptText.length);
                                if (substring === cleanExcerptText || 
                                    (cleanExcerptText.includes(substring) && substring.length >= minMatchLength)) {
                                    startIndex = i;
                                    matchFound = true;
                                    break;
                                }
                            }
                        }
                    }
                    
                    if (!matchFound && excerpt.analysis_uuid === file.uuid) {
                        // As a last resort, try to find the text anywhere in this file's content
                        startIndex = text.toLowerCase().indexOf(excerptText.toLowerCase().trim());
                        matchFound = startIndex !== -1;
                    }
                    
                    if (matchFound && startIndex !== -1) {
                        // Calculate the actual positions in the original text case
                        const beforeText = text.substring(0, startIndex);
                        const actualStart = beforeText.length;
                        const actualEnd = actualStart + excerptText.length;
                        
                        relevantExcerpts.push({
                            start: actualStart,
                            end: Math.min(actualEnd, text.length),
                            color: code.color,
                            codeName: code.name,
                            description: code.description,
                            excerptText: excerptText,
                            matchType: excerpt.transcription_uuid === lineData.uuid ? 'uuid' : 'text'
                        });
                    }
                });
            }
        });

        if (relevantExcerpts.length === 0) {
            return <span className="whitespace-pre-wrap">{text}</span>;
        }

        // Sort excerpts by start position and remove overlaps
        relevantExcerpts.sort((a, b) => a.start - b.start);
        
        // Remove overlapping excerpts, keeping the first one
        const nonOverlappingExcerpts = [];
        relevantExcerpts.forEach(excerpt => {
            const isOverlapping = nonOverlappingExcerpts.some(existing => 
                (excerpt.start >= existing.start && excerpt.start < existing.end) ||
                (excerpt.end > existing.start && excerpt.end <= existing.end)
            );
            
            if (!isOverlapping) {
                nonOverlappingExcerpts.push(excerpt);
            }
        });

        const result = [];
        let lastIndex = 0;
        let segmentCounter = 0;

        nonOverlappingExcerpts.forEach(excerpt => {
            // Add unselected text before the current excerpt
            if (excerpt.start > lastIndex) {
                const unselectedText = text.substring(lastIndex, excerpt.start);
                result.push(
                    <span key={`unsel-${segmentCounter++}`} className="whitespace-pre-wrap">
                        {unselectedText}
                    </span>
                );
            }

            // Add the highlighted excerpt
            const highlightedText = text.substring(excerpt.start, excerpt.end);
            result.push(
                <span 
                    key={`sel-${segmentCounter++}`}
                    className="rounded px-1 py-0.5 shadow-sm whitespace-pre-wrap cursor-help transition-opacity hover:opacity-100"
                    style={{ backgroundColor: excerpt.color || '#f3f4f6', opacity: 0.85 }}
                    title={`Code: ${excerpt.codeName}${excerpt.description ? '\nDescription: ' + excerpt.description : ''}\nMatch: ${excerpt.matchType}`}
                >
                    {highlightedText}
                </span>
            );

            lastIndex = Math.min(excerpt.end, text.length);
        });

        // Add any remaining unselected text
        if (lastIndex < text.length) {
            const remainingText = text.substring(lastIndex);
            result.push(
                <span key={`rem-${segmentCounter++}`} className="whitespace-pre-wrap">
                    {remainingText}
                </span>
            );
        }

        return result;
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center py-8">
                    <div className="text-gray-400 text-2xl mb-2">⏳</div>
                    <div className="text-gray-600">{t('PipelineResults.loadingTranscription')}</div>
                    <div className="text-sm text-gray-500">File: {file.name}</div>
                </div>
            </div>
        );
    }

    if (error || !transcriptionData) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center py-8">
                    <div className="text-red-400 text-2xl mb-2">❌</div>
                    <div className="text-red-600 mb-2">{t('PipelineResults.failedLoadTranscription')}</div>
                    <div className="text-sm text-gray-500 mb-4">{error}</div>
                    <a 
                        href={`/text_coder/${file.project_uuid || 'unknown'}/${file.uuid}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-amber-500 hover:underline"
                    >
                        {t('PipelineResults.tryOpeningTextCoder')}
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow">
            {/* File Header */}
            <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {file.file_type === "audio" && <SpeakerWaveIcon className="w-8 h-8 text-gray-600" />}
                        {file.file_type === "file" && <DocumentTextIcon className="w-8 h-8 text-gray-600" />}
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {file.name || `Transcription ${fileIndex + 1}`}
                            </h3>
                            {file.created_at && (
                                <div className="text-sm text-gray-500">
                                    {formatDateTime(file.created_at)}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <a 
                        href={`/text_coder/${file.project_uuid || 'unknown'}/${file.uuid}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm bg-black text-white px-4 py-2 rounded hover:bg-gray-800 transition-colors"
                    >
                        {t('PipelineResults.openTextCoder')}
                    </a>
                </div>
                
                {/* Code Legend */}
                {textCodes && textCodes.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('PipelineResults.codeLegend')}</h4>
                        <div className="flex flex-wrap gap-2">
                            {textCodes.map((code, index) => (
                                <div key={code.uuid || index} className="flex items-center gap-1 text-xs">
                                    <div 
                                        className="w-3 h-3 rounded border border-gray-300"
                                        style={{ backgroundColor: code.color || '#gray' }}
                                    ></div>
                                    <span className="text-gray-600">{code.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Transcription Content */}
            <div className="p-6">
                {/* Collapsible Toggle Button */}
                <div className="flex justify-between items-center mb-4">
                    <div className="text-sm text-gray-600">
                        {transcriptionData.length} transcription lines with highlighted codes
                    </div>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="inline-flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors print:hidden"
                    >
                        {isExpanded ? (
                            <>
                                <ChevronDownIcon className="w-4 h-4" />
                                <span>{t('PipelineResults.collapseTranscription')}</span>
                            </>
                        ) : (
                            <>
                                <ChevronRightIcon className="w-4 h-4" />
                                <span>{t('PipelineResults.expandTranscriptionLines', { count: transcriptionData.length })}</span>
                            </>
                        )}
                    </button>
                </div>

                <div className={`space-y-4 border rounded-lg bg-gray-50 p-4 transcription-content ${
                    isExpanded ? 'max-h-none overflow-visible' : 'max-h-96 overflow-y-auto'
                }`}>
                    {/* Show preview (first 10 lines) when collapsed, all when expanded */}
                    {transcriptionData.map((line, index) => (
                        <div 
                            key={line.uuid || index} 
                            className={`text-base mt-2 mb-4 ${
                                !isExpanded && index >= 10 ? 'hidden print:block' : ''
                            }`}
                        >
                            <div className="text-base mt-2 flex flex-col">
                                {/* Speaker and Time - Horizontal Layout like TextCoder */}
                                <div className="flex gap-2 items-center mb-2">
                                    {/* Speaker Name in Colored Pill */}
                                    <div 
                                        className="text-black rounded-full px-4 py-1 text-sm w-fit"
                                        style={{ backgroundColor: getSpeakerColor(speakerNames, speakerNames[line.speakerKey], colors) || '#A7C7E7' }}
                                    >
                                        {line.speakerDisplayName}
                                    </div>
                                    
                                    {/* Time Range */}
                                    {(line.startTime && line.endTime) && (
                                        <div className="cursor-pointer underline text-gray-400 flex gap-1 text-sm items-center">
                                            {newFormatTime(line.startTime)} - {newFormatTime(line.endTime)}
                                        </div>
                                    )}
                                </div>
                                
                                {/* Transcription Text with Highlights */}
                                <div className="mt-2 ml-2">
                                    <span className="text-sm text-gray-800 leading-relaxed">
                                        {renderTextWithHighlights(line, line.editablePart)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {/* Show "more lines" indicator when collapsed */}
                    {!isExpanded && transcriptionData.length > 10 && (
                        <div className="text-center py-4 border-t border-gray-200 mt-4 print:hidden">
                            <div className="text-gray-500 text-sm">
                                ... {transcriptionData.length - 10} {t('PipelineResults.moreLines')}
                            </div>
                        </div>
                    )}
                </div>
                
                {transcriptionData.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        <div className="text-gray-400 text-2xl mb-2">📝</div>
                        <div>{t('PipelineResults.noTranscriptionContent')}</div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Helper function to enrich text codes with file references
const enrichTextCodesWithFileReferences = (textCodes, analysisData, analysisParents, setLoadingStep = () => {}, t = () => {}) => {
    let enrichedCount = 0;
    let notFoundCount = 0;
    
    // Helper function to normalize text for comparison
    const normalizeText = (text) => {
        if (!text) return '';
        return text
            .toLowerCase()
            .replace(/\s+/g, ' ')  // Replace multiple whitespace with single space
            .replace(/[^\w\s]/g, '') // Remove punctuation
            .trim();
    };
    
    
    const totalCodes = textCodes.length;
    const enrichedCodes = textCodes.map((code, index) => {
        if (index % Math.ceil(totalCodes / 5) === 0) {
            const progress = Math.round((index / totalCodes) * 100);
            setLoadingStep(t('PipelineResults.processingCodesProgress', { progress }));
        }
        
        if (!code.excerpts || code.excerpts.length === 0) {
            return code;
        }
        
        const enrichedExcerpts = code.excerpts.map(excerpt => {
            if (excerpt.analysis_uuid && excerpt.analysis_uuid !== null) {
                return excerpt;
            }
            
            let foundAnalysisUuid = null;
            
            // Method 1: Enhanced text search with normalization
            if (excerpt.text && excerpt.text.length > 10) {
                const excerptTextNormalized = normalizeText(excerpt.text);
                const searchTextShort = excerptTextNormalized.substring(0, 30); // Shorter for better matching
                const searchTextLong = excerptTextNormalized.substring(0, 80); // Longer for precision
                
                
                for (const [analysisUuid, data] of Object.entries(analysisData)) {
                    if (data?.fragments?.edited_transcription) {
                        const transcriptionNormalized = normalizeText(data.fragments.edited_transcription);
                        
                        // Try both short and long search texts
                        if (transcriptionNormalized.includes(searchTextShort) || 
                            transcriptionNormalized.includes(searchTextLong)) {
                            foundAnalysisUuid = analysisUuid;
                            break;
                        }
                    }
                }
                
                // Method 2: Try word-by-word matching if exact match fails
                if (!foundAnalysisUuid && excerptTextNormalized.length > 20) {
                    const words = excerptTextNormalized.split(' ').filter(w => w.length > 3).slice(0, 5);
                    
                    if (words.length >= 3) {
                        for (const [analysisUuid, data] of Object.entries(analysisData)) {
                            if (data?.fragments?.edited_transcription) {
                                const transcriptionNormalized = normalizeText(data.fragments.edited_transcription);
                                
                                // Check if at least 3 significant words appear in order
                                let wordsFound = 0;
                                let lastIndex = 0;
                                
                                for (const word of words) {
                                    const wordIndex = transcriptionNormalized.indexOf(word, lastIndex);
                                    if (wordIndex > lastIndex) {
                                        wordsFound++;
                                        lastIndex = wordIndex;
                                    }
                                }
                                
                                if (wordsFound >= Math.min(3, words.length)) {
                                    foundAnalysisUuid = analysisUuid;
                                    break;
                                }
                            }
                        }
                    }
                }
            }
            
            if (foundAnalysisUuid) {
                enrichedCount++;
                return {
                    ...excerpt,
                    analysis_uuid: foundAnalysisUuid,
                    analysis_parent_id: foundAnalysisUuid
                };
            } else {
                notFoundCount++;
                return excerpt;
            }
        });
        
        return {
            ...code,
            excerpts: enrichedExcerpts
        };
    });
    
    
    return enrichedCodes;
};

function PipelineResults() {
    const { pipeline_uuid } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [pipeline, setPipeline] = useState(null);
    const [project, setProject] = useState(null);
    const [articles, setArticles] = useState([]);
    const [bibliography, setBibliography] = useState([]);
    const [textCodes, setTextCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingStep, setLoadingStep] = useState(t('PipelineResults.initializing'));
    const [componentsReady, setComponentsReady] = useState(false);
    
    // Collapsible sections state
    const [expandedSections, setExpandedSections] = useState({
        article: true,
        results: true,
        bibliography: true,
        analysis: true,
        transcriptions: true
    });

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoadingStep(t('PipelineResults.loadingPipelineData'));
                
                // Use the new single endpoint with include_full_data=true
                const response = await fetch(
                    `${process.env.REACT_APP_API_BASE_URL}/research-pipeline/${pipeline_uuid}?include_full_data=true`,
                    {
                        headers: {
                            'Authorization': `Bearer ${getToken()}`,
                            'Accept': 'application/json'
                        }
                    }
                );
                
                const responseData = await response.json();
                
                if (responseData.success && responseData.data) {
                    const { pipeline: pipelineData, project, articles, saved_articles, text_codes, analysis_data } = responseData.data;
                    
                    setLoadingStep(t('PipelineResults.processingConfig'));
                    // Set basic data first
                    setPipeline(pipelineData);
                    setProject(project);
                    setArticles(articles || []);
                    setBibliography(saved_articles || []);
                    
                    // Store analysis data globally for components to use
                    window.pipelineAnalysisData = analysis_data || {};
                    
                    setLoadingStep(t('PipelineResults.processingCodes'));
                    // Delay to allow UI to update
                    await new Promise(resolve => setTimeout(resolve, 200));
                    
                    // Enrich text codes with file references for excerpts
                    const enrichedCodes = enrichTextCodesWithFileReferences(text_codes || [], analysis_data || {}, project?.analysis_parents || [], setLoadingStep, t);
                    
                    setLoadingStep(t('PipelineResults.finalizingData'));
                    // Delay to allow processing
                    await new Promise(resolve => setTimeout(resolve, 300));
                    
                    setTextCodes(enrichedCodes);
                    
                    // Final delay to ensure all components have time to process the data
                    setLoadingStep(t('PipelineResults.preparingInterface'));
                    await new Promise(resolve => setTimeout(resolve, 200));
                    
                    // Wait for React to commit all updates to DOM
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    // Mark components as ready
                    setComponentsReady(true);
                    
                    // Additional wait to ensure DOM is ready and all components can render
                    // This prevents lazy loading on scroll
                    await new Promise(resolve => {
                        // Use requestAnimationFrame to wait for next paint
                        requestAnimationFrame(() => {
                            // Wait longer for heavy components (charts, transcriptions) to fully initialize
                            // This ensures no lazy loading happens when scrolling
                            setTimeout(resolve, 1500);
                        });
                    });
                    
                    // Final check to ensure everything is ready
                    await new Promise(resolve => {
                        requestAnimationFrame(() => {
                            resolve();
                        });
                    });
                    
                } else {
                    // Pipeline response was not successful
                }
            } catch (err) {
                // Failed to load pipeline data
            } finally {
                // Only set loading to false after all delays
                setLoading(false);
            }
        };

        loadData();
    }, [pipeline_uuid]);

    // Handle print events to expand all sections
    useEffect(() => {
        const handleBeforePrint = () => {
            // Temporarily expand all sections for printing
            setExpandedSections({
                article: true,
                results: true,
                bibliography: true,
                analysis: true,
                transcriptions: true
            });
        };

        const handleAfterPrint = () => {
            // Note: We don't collapse sections after print to avoid disrupting user experience
            // Users can manually collapse sections if needed
        };

        // Add event listeners for print
        window.addEventListener('beforeprint', handleBeforePrint);
        window.addEventListener('afterprint', handleAfterPrint);

        return () => {
            window.removeEventListener('beforeprint', handleBeforePrint);
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, []);
    
    const toggleSection = (sectionKey) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Renders a verify-and-flag badge for a bibliography entry based on its verification status.
    const renderVerificationBadge = (item) => {
        const status = item.is_retracted ? 'retracted' : (item.verification_status || 'unverified');
        const styles = {
            verified: 'bg-green-100 text-green-800',
            retracted: 'bg-red-100 text-red-800',
            unverified_flagged: 'bg-amber-100 text-amber-800',
            unverified: 'bg-gray-100 text-gray-600',
        };
        const labels = {
            verified: t('PipelineResults.verificationVerified', 'Verified'),
            retracted: t('PipelineResults.verificationRetracted', 'Retracted'),
            unverified_flagged: t('PipelineResults.verificationFlagged', 'Unverified'),
            unverified: t('PipelineResults.verificationUnverified', 'Not checked'),
        };
        return (
            <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.unverified}`}>
                {labels[status] || labels.unverified}
            </span>
        );
    };

    const handlePrint = async () => {
        // First, expand all sections to ensure all content is visible
        setExpandedSections({
            article: true,
            results: true,
            bibliography: true,
            analysis: true,
            transcriptions: true
        });

        // Wait for DOM updates and chart rendering
        await new Promise(resolve => {
            setTimeout(() => {
                // Additional wait for charts and complex components
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        window.print();
                        resolve();
                    }, 1000); // 1 second for charts to fully render
                });
            }, 500); // 0.5 second for sections to expand
        });
    };


    if (loading) {
        return (
            <div className="md:pl-64 py-12 w-full flex justify-center items-center min-h-screen">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="mb-8">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-black rounded-full mb-4">
                            <ParticleLoader size={32} tone="paper" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('PipelineResults.loadingTitle')}</h2>
                        <p className="text-gray-600 mb-6">
                            {t('PipelineResults.loadingTitle')}
                        </p>
                        <div className="bg-gray-200 rounded-full h-2 mb-4">
                            <div className="bg-black h-2 rounded-full transition-all duration-500 ease-out" style={{width: '75%'}}></div>
                        </div>
                        <p className="text-sm text-gray-500 font-medium">{loadingStep}</p>
                    </div>
                    <div className="text-xs text-gray-400 space-y-1">
                        <p>{t('PipelineResults.fetchingConfigStep')}</p>
                        <p>{t('PipelineResults.loadingAnalysisStep')}</p>
                        <p className="opacity-50">{t('PipelineResults.processingExcerptsStep')}</p>
                        <p className="opacity-50">{t('PipelineResults.preparingVisualizationsStep')}</p>
                    </div>
                </div>
            </div>
        );
    }

    const files = project?.analysis_parents || [];

    return (
        <div className="md:pl-64 py-12 w-full flex justify-center">
            <style>{`
                /* Hide all export/download/edit options in research results section */
                .research-results-readonly .edit-button,
                .research-results-readonly button[aria-label*="download"],
                .research-results-readonly button[aria-label*="export"],
                .research-results-readonly [class*="download"],
                .research-results-readonly [class*="export"],
                .research-results-readonly .export-menu,
                .research-results-readonly .dropdown-menu,
                .research-results-readonly svg[data-slot="icon"][class*="download"],
                .research-results-readonly svg[data-slot="icon"][class*="arrow-down"],
                .research-results-readonly .menu-button,
                .research-results-readonly button:has(svg[class*="ArrowDownTray"]),
                .research-results-readonly button:has(svg[data-testid*="download"]),
                .research-results-readonly [title*="download"],
                .research-results-readonly [title*="export"],
                .research-results-readonly [title*="Download"],
                .research-results-readonly [title*="Export"] {
                    display: none !important;
                }
                
                /* Hide specific Heroicons that are used for download/export */
                .research-results-readonly svg[data-slot="icon"]:has([d*="M3 16.5v2.25A2.25"]),
                .research-results-readonly svg[data-slot="icon"]:has([d*="M12 9.75v"]) {
                    display: none !important;
                }
                
                /* Hide parent buttons that contain download icons */
                .research-results-readonly button:has(svg[class*="ArrowDownTrayIcon"]) {
                    display: none !important;
                }

                /* Print styles */
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    
                    /* Only show the main content when printing */
                    .print-content, .print-content * {
                        visibility: visible;
                    }
                    
                    .print-content {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        padding: 20px;
                        background: white;
                    }
                    
                    /* Hide navigation, buttons, and other UI elements */
                    .md\\:pl-64 {
                        padding-left: 0 !important;
                    }
                    
                    /* Hide interactive buttons but keep section headers */
                    button:not(.section-header), .hover\\:bg-gray-100 {
                        display: none !important;
                    }
                    
                    /* Style section header buttons for print */
                    button.section-header {
                        background: none !important;
                        border: none !important;
                        padding: 0 !important;
                        margin-bottom: 1rem !important;
                        display: block !important;
                        width: 100% !important;
                        text-align: left !important;
                        cursor: default !important;
                    }
                    
                    /* Hide chevron icons in section headers */
                    button.section-header svg {
                        display: none !important;
                    }
                    
                    /* Force ALL sections to be fully expanded when printing */
                    .transcription-content {
                        max-height: none !important;
                        overflow: visible !important;
                    }
                    
                    /* Show all hidden content when printing */
                    .hidden.print\\:block {
                        display: block !important;
                    }
                    
                    .hidden.print\\:inline-block {
                        display: inline-block !important;
                    }
                    
                    /* Force all collapsible sections to be visible for print */
                    .print-expand-all .animate-in,
                    .print-expand-all [class*="slide-in-from-top"],
                    .print-expand-all [class*="duration-300"] {
                        display: block !important;
                        visibility: visible !important;
                        transform: none !important;
                        opacity: 1 !important;
                        height: auto !important;
                        overflow: visible !important;
                    }
                    
                    /* Ensure section content is visible */
                    .print-expand-all section > div:last-child {
                        display: block !important;
                    }
                    
                    /* Hide print-only elements */
                    .print\\:hidden {
                        display: none !important;
                    }
                    
                    /* Optimize spacing for print */
                    .py-12 {
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    }
                    
                    .space-y-16 > * + * {
                        margin-top: 2rem !important;
                    }
                    
                    .shadow, .shadow-xl, .shadow-lg {
                        box-shadow: none !important;
                        border: 1px solid #e5e7eb !important;
                    }
                    
                    /* Ensure charts and tables fit well on print */
                    .overflow-x-auto {
                        overflow: visible !important;
                    }
                    
                    table {
                        font-size: 0.875rem !important;
                    }
                    
                    /* Page breaks */
                    .break-before-page {
                        page-break-before: always;
                    }
                    
                    .break-inside-avoid {
                        page-break-inside: avoid;
                    }
                    
                    /* Hide outlines and shadows when printing */
                    * {
                        outline: none !important;
                        text-shadow: none !important;
                    }
                    
                    /* Remove decorative borders but keep essential ones */
                    .shadow, .shadow-xl, .shadow-lg, .shadow-md, .shadow-sm {
                        box-shadow: none !important;
                    }
                    
                    /* Remove card and container borders */
                    .border, .border-t, .border-b, .border-l, .border-r,
                    .border-gray-200, .border-gray-300, .border-gray-100 {
                        border: none !important;
                    }
                    
                    /* Remove rounded corners on containers */
                    .rounded, .rounded-lg, .rounded-md, .rounded-full {
                        border-radius: 0 !important;
                    }
                    
                    /* Remove background colors on containers but keep chart backgrounds and code highlights */
                    .bg-gray-50:not(.transcription-content *), 
                    .bg-gray-100:not(.transcription-content *), 
                    .bg-gray-200:not(.transcription-content *) {
                        background-color: white !important;
                    }
                    
                    /* Preserve text code highlights in transcriptions for print */
                    .transcription-content span[style*="backgroundColor"],
                    .transcription-content span[style*="background-color"] {
                        -webkit-print-color-adjust: exact !important;
                        color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        border: 1px solid #999 !important;
                        padding: 1px 3px !important;
                    }
                    
                    /* Force browser to print background colors for highlighted text */
                    * {
                        -webkit-print-color-adjust: exact !important;
                        color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    
                    /* Keep essential table borders for readability */
                    table, th, td {
                        border: 1px solid #e5e7eb !important;
                    }
                    
                    /* Fix joint analysis section for print */
                    .grid {
                        display: block !important;
                    }
                    
                    .grid > * {
                        width: 100% !important;
                        margin-bottom: 2rem !important;
                        page-break-inside: avoid !important;
                    }
                    
                    /* Keep only sentiment and participation cards side by side for print */
                    .analysis-cards.grid.grid-cols-1.md\\:grid-cols-2 > .flex-1:first-child,
                    .analysis-cards.grid.grid-cols-1.md\\:grid-cols-2 > .flex-1:nth-child(2) {
                        display: inline-block !important;
                        width: 49% !important;
                        margin-right: 1% !important;
                        margin-bottom: 1rem !important;
                        vertical-align: top !important;
                    }
                    
                    .analysis-cards.grid.grid-cols-1.md\\:grid-cols-2 > .flex-1:nth-child(2) {
                        margin-right: 0 !important;
                    }
                    
                    /* Keep other cards (col-span-2) full width and stacked */
                    .analysis-cards.grid.grid-cols-1.md\\:grid-cols-2 > .col-span-2 {
                        width: 100% !important;
                        display: block !important;
                        margin-bottom: 2rem !important;
                    }
                    
                    /* Remove all dark backgrounds */
                    .bg-black, .bg-gray-800, .bg-gray-900 {
                        background-color: white !important;
                        color: black !important;
                    }
                    
                    /* Remove all shadows completely */
                    * {
                        box-shadow: none !important;
                        -webkit-box-shadow: none !important;
                        -moz-box-shadow: none !important;
                    }
                    
                    /* Preserve chart and graph elements */
                    svg, canvas, .recharts-wrapper, [data-chart] {
                        border: none !important;
                        background: white !important;
                        box-shadow: none !important;
                    }
                    
                    /* Keep chart colors and styles */
                    svg *, canvas *, .recharts-wrapper * {
                        opacity: 1 !important;
                    }
                    
                    /* Fix card layouts in analysis section */
                    .analysis-cards, .grid-cols-1, .grid-cols-2, .md\\:grid-cols-2 {
                        display: block !important;
                        grid-template-columns: none !important;
                    }
                    
                    /* Stack analysis cards vertically */
                    .analysis-cards > *, .grid > * {
                        display: block !important;
                        width: 100% !important;
                        margin-bottom: 2rem !important;
                        break-inside: avoid !important;
                    }
                }
            `}</style>
            <div className="flex flex-col w-full max-w-7xl mx-auto px-4 print-content print-expand-all">

                {/* Pipeline Info */}
                {pipeline && (
                    <div className="mb-8 bg-white rounded-lg shadow p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('PipelineResults.pageTitle')}</h1>
                                <h2 className="text-xl font-semibold text-gray-700">{pipeline.name}</h2>
                            </div>
                            <div className="flex flex-col gap-2">
                                <button
                                    onClick={handlePrint}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
                                    title={t('PipelineResults.printButtonTitle')}
                                >
                                    <PrinterIcon className="w-4 h-4" />
                                    <span>{t('PipelineResults.printButtonText')}</span>
                                </button>
                                <button
                                    onClick={() => navigate(`/project/${pipeline.project?.uuid}`)}
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-white text-black border-2 border-black rounded-md hover:bg-gray-50 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                    <span>{t('ResearchPipeline.viewProject')}</span>
                                </button>
                            </div>
                        </div>
                        {pipeline.research_description && (
                            <p className="text-gray-600">{pipeline.research_description}</p>
                        )}
                        <div className="mt-4 flex gap-4 text-sm text-gray-500">
                            <span>{t('PipelineResults.status')}: <span className="font-medium text-green-600">{t(`PipelineResults.${pipeline.status}`)}</span></span>
                        </div>
                    </div>
                )}

                <div className="space-y-16">
                    {/* Section 1: Final Article */}
                    <section>
                        <button
                            onClick={() => toggleSection('article')}
                            className="section-header w-full flex items-center justify-between text-2xl font-bold text-gray-900 mb-6 hover:text-gray-700 transition-colors"
                        >
                            <span>{t('PipelineResults.articleInfo')}</span>
                            {expandedSections.article ? 
                                <ChevronDownIcon className="w-6 h-6" /> : 
                                <ChevronRightIcon className="w-6 h-6" />
                            }
                        </button>
                        {expandedSections.article && (
                            <div className="bg-white rounded-lg shadow p-6 animate-in slide-in-from-top duration-300">
                            {articles && articles.length > 0 ? (
                                <div className="space-y-8">
                                    {articles.map((article, index) => (
                                        <div key={article.id || index} className="border-b border-gray-200 pb-6 last:border-b-0">
                                            <h3 className="text-3xl font-bold text-gray-900 mb-4">
                                                {pipeline?.name || article.title || article.name || article.article_title || article.article_name || `Article ${index + 1}`}
                                            </h3>
                                            {article.article_details && (
                                                <p className="text-gray-600 mb-4 italic">
                                                    {article.article_details}
                                                </p>
                                            )}
                                            
                                            {article.content && (
                                                <div className="prose max-w-none mb-6">
                                                    <div className="whitespace-pre-wrap text-gray-700">
                                                        {article.content}
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {article.sections && article.sections.length > 0 && (
                                                <div className="space-y-6">
                                                    {article.sections
                                                        .sort((a, b) => a.order_index - b.order_index)
                                                        .map((section, sectionIndex) => (
                                                        <div key={section.id || sectionIndex}>
                                                            {section.section_title && (
                                                                <h4 className="text-lg font-medium text-gray-800 mb-3">
                                                                    {section.section_title}
                                                                </h4>
                                                            )}
                                                            {section.output && (
                                                                <div className="prose max-w-none">
                                                                    <ReactMarkdown 
                                                                        remarkPlugins={[remarkGfm]}
                                                                        className="text-gray-700 leading-relaxed"
                                                                    >
                                                                        {section.output}
                                                                    </ReactMarkdown>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {!article.content && (!article.sections || article.sections.length === 0) && (
                                                <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
                                                    <div className="flex items-center">
                                                        <div className="text-yellow-400 mr-2">⚠️</div>
                                                        <div>
                                                            <div className="font-medium text-yellow-800">Article completed but content not available</div>
                                                            <div className="text-yellow-700 text-sm">
                                                                The article generation is marked as completed but the content is not available in this view.
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="text-gray-400 text-4xl mb-4">📄</div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">{t('PipelineResults.noFinalArticle')}</h3>
                                    <p className="text-gray-500">{t('PipelineResults.noArticlesGenerated')}</p>
                                </div>
                            )}
                            </div>
                        )}
                    </section>

                    {/* Section 2: Research Results */}
                    <section>
                        <button
                            onClick={() => toggleSection('results')}
                            className="section-header w-full flex items-center justify-between text-2xl font-bold text-gray-900 mb-6 hover:text-gray-700 transition-colors"
                        >
                            <span>{t('CategoryResults.Title')}</span>
                            {expandedSections.results ? 
                                <ChevronDownIcon className="w-6 h-6" /> : 
                                <ChevronRightIcon className="w-6 h-6" />
                            }
                        </button>
                        {expandedSections.results && (
                            <div className="bg-white rounded-lg shadow p-6 animate-in slide-in-from-top duration-300 research-results-readonly">
                            {textCodes && textCodes.length > 0 ? (
                                <div className="space-y-8">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="bg-amber-50 rounded p-4 text-center">
                                            <div className="text-2xl font-bold text-amber-700">{textCodes.length}</div>
                                            <div className="text-sm font-medium text-amber-900">{t('PipelineResults.totalCodes')}</div>
                                        </div>
                                        <div className="bg-green-50 rounded p-4 text-center">
                                            <div className="text-2xl font-bold text-green-700">{files.length}</div>
                                            <div className="text-sm font-medium text-green-900">{t('PipelineResults.filesAnalyzed')}</div>
                                        </div>
                                        <div className="bg-purple-50 rounded p-4 text-center">
                                            <div className="text-2xl font-bold text-purple-700">
                                                {textCodes.reduce((total, code) => total + (code.excerpts?.length || 0), 0)}
                                            </div>
                                            <div className="text-sm font-medium text-purple-900">{t('PipelineResults.totalExcerpts')}</div>
                                        </div>
                                    </div>

                                    {/* Detailed Code Table */}
                                    <div className="space-y-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('PipelineResults.detailedCodeAnalysis')}</h3>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                                                    <thead className="bg-gray-50">
                                                        <tr>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                                {t('PipelineResults.tableHeaders.code')}
                                                            </th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                                {t('PipelineResults.tableHeaders.description')}
                                                            </th>
                                                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                                {t('PipelineResults.tableHeaders.sampleExcerpts')}
                                                            </th>
                                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                                {t('PipelineResults.tableHeaders.numExcerpts')}
                                                            </th>
                                                            <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                                {t('PipelineResults.tableHeaders.numFiles')}
                                                            </th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {textCodes.map((code, index) => {
                                                            // Get unique files - excerpts should now have analysis_uuid
                                                            const uniqueFileIds = new Set();
                                                            if (code.excerpts && Array.isArray(code.excerpts)) {
                                                                code.excerpts.forEach(excerpt => {
                                                                    if (excerpt.analysis_uuid) {
                                                                        uniqueFileIds.add(excerpt.analysis_uuid);
                                                                    }
                                                                });
                                                            }
                                                            
                                                            // Map to file names
                                                            const uniqueFiles = new Set();
                                                            uniqueFileIds.forEach(analysisUuid => {
                                                                const file = files.find(f => f.uuid === analysisUuid);
                                                                if (file) {
                                                                    uniqueFiles.add(file.name || file.uuid);
                                                                }
                                                            });
                                                            
                                                            return (
                                                                <tr key={code.uuid || index} className="hover:bg-gray-50">
                                                                    <td className="px-4 py-3 border-b">
                                                                        <div className="flex items-center gap-2">
                                                                            <div 
                                                                                className="w-3 h-3 rounded border border-gray-300 flex-shrink-0"
                                                                                style={{ backgroundColor: code.color || '#gray' }}
                                                                            ></div>
                                                                            <span className="font-medium text-gray-900">{code.name}</span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 border-b">
                                                                        <p className="text-sm text-gray-600 max-w-xs">
                                                                            {code.description || t('PipelineResults.noDescription')}
                                                                        </p>
                                                                    </td>
                                                                    <td className="px-4 py-3 border-b">
                                                                        <div className="space-y-1 max-w-md">
                                                                            {code.excerpts && code.excerpts.length > 0 ? (
                                                                                code.excerpts.slice(0, 2).map((excerpt, excerptIndex) => {
                                                                                    // Get excerpt text directly from text_codes data
                                                                                    let text = excerpt.text || excerpt.content || excerpt.excerpt_text || excerpt.body;
                                                                                    
                                                                                    if (!text) {
                                                                                        // Show available excerpt data for debugging
                                                                                        const availableFields = Object.keys(excerpt).filter(key => excerpt[key]).join(', ');
                                                                                        text = `Excerpt fields: ${availableFields}`;
                                                                                    }
                                                                                
                                                                                const truncatedText = text.length > 100 ? 
                                                                                    text.substring(0, 100) + '...' : text;
                                                                                    return (
                                                                                        <div 
                                                                                            key={excerptIndex} 
                                                                                            className="text-xs text-gray-600 bg-gray-50 rounded p-2 border-l-2"
                                                                                            style={{ borderLeftColor: code.color || '#gray' }}
                                                                                        >
                                                                                            {truncatedText ? `"${truncatedText}"` : 
                                                                                             <span className="italic text-gray-400">Excerpt content not available</span>}
                                                                                            {(() => {
                                                                                                // For enriched codes, use the file_name from the processed data
                                                                                                // For original codes, try to find file name from analysis_uuid
                                                                                                const fileName = excerpt.file_name || 
                                                                                                    (excerpt.analysis_uuid && files.find(f => f.uuid === excerpt.analysis_uuid)?.name);
                                                                                                return fileName && (
                                                                                                    <div className="mt-1 text-xs text-gray-400">
                                                                                                        {t('PipelineResults.from')}: {fileName}
                                                                                                    </div>
                                                                                                );
                                                                                            })()}
                                                                                        </div>
                                                                                    );
                                                                                })
                                                                            ) : (
                                                                                <span className="text-xs text-gray-400">No excerpts</span>
                                                                            )}
                                                                            {code.excerpts && code.excerpts.length > 2 && (
                                                                                <div className="text-xs text-gray-400">
                                                                                    +{code.excerpts.length - 2} {t('PipelineResults.more')}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-4 py-3 border-b text-center">
                                                                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                                                                            {code.excerpts?.length || 0}
                                                                        </span>
                                                                    </td>
                                                                    <td className="px-4 py-3 border-b text-center">
                                                                        <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                                                                            {uniqueFiles.size}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>

                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t('PipelineResults.codePresenceMatrix')}</h3>
                                            <div data-chart data-chart-id="presence-matrix" data-chart-title={t('PipelineResults.interviewPresenceMatrix')} data-chart-type="matrix">
                                                <ResearchPipelinePresenceMatrix
                                                    project={project}
                                                    codes={textCodes}
                                                    title={t('PipelineResults.interviewPresenceMatrix')}
                                                />
                                            </div>
                                        </div>

                                        {files.length > 0 && textCodes && textCodes.length > 0 && (
                                            <>
                                                <ErrorBoundary minimal>
                                                    <div data-chart data-chart-id="category-frequency-overview" data-chart-title={t('PipelineResults.categoryFrequencyOverview')} data-chart-type="polar-area">
                                                        <PolarAreaCenterLabelsChart
                                                            codes={textCodes}
                                                            files={files}
                                                            mode="codes-per-interview"
                                                            title={t('PipelineResults.categoryFrequencyOverview')}
                                                        />
                                                    </div>
                                                </ErrorBoundary>

                                                <ErrorBoundary minimal>
                                                    <div data-chart data-chart-id="code-frequency-by-file" data-chart-title={t('PipelineResults.codeFrequencyByFile')} data-chart-type="polar-charts">
                                                        <ResearchPipelinePolarCharts
                                                            codes={textCodes}
                                                            files={files}
                                                            title={t('PipelineResults.codeFrequencyByFile')}
                                                        />
                                                    </div>
                                                </ErrorBoundary>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="text-gray-400 text-4xl mb-4">📊</div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Research Results</h3>
                                    <p className="text-gray-500">No analysis results or codes found for this project.</p>
                                </div>
                            )}
                            </div>
                        )}
                    </section>

                    {/* Section 3: Bibliography */}
                    <section>
                        <button
                            onClick={() => toggleSection('bibliography')}
                            className="section-header w-full flex items-center justify-between text-2xl font-bold text-gray-900 mb-6 hover:text-gray-700 transition-colors"
                        >
                            <span>{t('DetailProjects.Bibliography')}</span>
                            {expandedSections.bibliography ? 
                                <ChevronDownIcon className="w-6 h-6" /> : 
                                <ChevronRightIcon className="w-6 h-6" />
                            }
                        </button>
                        {expandedSections.bibliography && (
                            <div className="bg-white rounded-lg shadow p-6 animate-in slide-in-from-top duration-300">
                            {bibliography && bibliography.length > 0 ? (
                                <div className="space-y-4">
                                    {bibliography.map((item, index) => (
                                        <div key={item.id || index} className="border-b border-gray-200 pb-4 last:border-b-0">
                                            <div className="flex items-start justify-between gap-2 mb-2">
                                                <h4 className="font-medium text-lg text-gray-900">{item.title || t('PipelineResults.untitled')}</h4>
                                                {renderVerificationBadge(item)}
                                            </div>
                                            {item.authors && (
                                                <p className="text-gray-600 text-sm mb-1">
                                                    {t('PipelineResults.authors')}: {
                                                        Array.isArray(item.authors) 
                                                            ? item.authors.map(author => 
                                                                typeof author === 'string' ? author : author.name
                                                              ).join(', ')
                                                            : typeof item.authors === 'string' 
                                                                ? item.authors
                                                                : item.authors.name || JSON.stringify(item.authors)
                                                    }
                                                </p>
                                            )}
                                            {item.journal && (
                                                <p className="text-gray-600 text-sm mb-1">{t('PipelineResults.journal')}: {item.journal}</p>
                                            )}
                                            {item.doi && (
                                                <p className="text-gray-600 text-sm mb-1">
                                                    {t('PipelineResults.doi')}: 
                                                    <a 
                                                        href={item.doi.startsWith('http') ? item.doi : `https://doi.org/${item.doi}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-amber-500 hover:underline ml-1"
                                                    >
                                                        {item.doi}
                                                    </a>
                                                </p>
                                            )}
                                            {item.url && (
                                                <p className="text-gray-600 text-sm">
                                                    URL: <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:underline">{item.url}</a>
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12">
                                    <div className="text-gray-400 text-4xl mb-4">📚</div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Bibliography</h3>
                                    <p className="text-gray-500">No bibliography entries found for this project.</p>
                                </div>
                            )}
                            </div>
                        )}
                    </section>

                    {/* Section 4: Analysis Results */}
                    <section>
                        <button
                            onClick={() => toggleSection('analysis')}
                            className="section-header w-full flex items-center justify-between text-2xl font-bold text-gray-900 mb-6 hover:text-gray-700 transition-colors"
                        >
                            <span>{t('Analysis.AnalysisTitle')}</span>
                            {expandedSections.analysis ? 
                                <ChevronDownIcon className="w-6 h-6" /> : 
                                <ChevronRightIcon className="w-6 h-6" />
                            }
                        </button>
                        
                        {expandedSections.analysis && (
                            <>
                            {files.length > 0 ? (
                            <div className="space-y-12">
                                {files.map((file, index) => (
                                    <IndividualAnalysis 
                                        key={file.uuid} 
                                        file={file} 
                                        index={index} 
                                        t={t} 
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="bg-white rounded-lg shadow p-6">
                                <div className="text-center py-12">
                                    <div className="text-gray-400 text-4xl mb-4">🔍</div>
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Results</h3>
                                    <p className="text-gray-500">No analysis results found for this project.</p>
                                </div>
                            </div>
                        )}
                            </>
                        )}
                    </section>

                    {/* Section 5: Full Transcriptions with Coded Highlights */}
                    <section>
                        <button
                            onClick={() => toggleSection('transcriptions')}
                            className="section-header w-full flex items-center justify-between text-2xl font-bold text-gray-900 mb-6 hover:text-gray-700 transition-colors"
                        >
                            <span>{t('TranscriptionModal.TranscriptionLabel')} {t('TextCoder.Codes')}</span>
                            {expandedSections.transcriptions ? 
                                <ChevronDownIcon className="w-6 h-6" /> : 
                                <ChevronRightIcon className="w-6 h-6" />
                            }
                        </button>
                        {expandedSections.transcriptions && (
                            <div className="space-y-8 animate-in slide-in-from-top duration-300">
                                {files && files.length > 0 ? (
                                    files.map((file, fileIndex) => (
                                        <FullTranscriptionDisplay 
                                            key={file.uuid}
                                            file={file}
                                            textCodes={textCodes}
                                            fileIndex={fileIndex}
                                            t={t}
                                        />
                                    ))
                                ) : (
                                    <div className="bg-white rounded-lg shadow p-6">
                                        <div className="text-center py-12">
                                            <div className="text-gray-400 text-4xl mb-4">📝</div>
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Transcriptions</h3>
                                            <p className="text-gray-500">No transcriptions found for this project.</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                </div>
            </div>

        </div>
    );
}

export default PipelineResults;